import { publicEnrollmentSessionService } from '../utils/PublicEnrollmentSessionService';

class FetchBrary {
    formatPagedUrl(url, attr) {
        const { page, itemsPerPage, sort } = attr;

        let sortQuery = "";
        if (sort) {
            sortQuery = "&";
            sort.forEach(s => {
                sortQuery += `sort=${s.columnName} ${s.order}&`;
            });
        }

        url += `?currentPage=${page}&itemsPerPage=${itemsPerPage}${sortQuery}`;
        return url;
    }

    /** Usage of this function should be limited to very few places, mostly at the lowest level possible. */
    async _getAsHttpResponse(url, attr) {
        if(attr) {
            url = this.formatPagedUrl(url, attr);
        }

        const token = localStorage.getItem('access_token');

        const headers = new Headers({
            'Authorization': `Bearer ${token}`,
            'Cache-Control': 'no-cache',
            'Pragma': 'no-cache'
        });

        this.addStandardHeaderValues(headers);

        const response = await fetch(url, {
            headers: headers
        });

        if (response.ok) {
            return response;
        }
        else {
            throw this.buildErrorFromResponse(response);
        }
    }

    async get(url, attr) {
        const response = await this._getAsHttpResponse(url, attr);
        
        const json = await response.json();

        if (attr && json !== null && typeof json === 'object') {
            const { page, sort } = attr;
            json.currentPage = page;
            json.sort = sort ? sort : [];
        }

        return json;
    }

    addStandardHeaderValues(headers) {
        const publicSession = publicEnrollmentSessionService.getPublicSession();

        if (publicSession !== null) {
            headers.append('Public-Enrollment-PersonId', publicSession.employeePersonId);
            headers.append('Public-Enrollment-PackageId', publicSession.enrollmentPackageId);
            headers.append('Public-Enrollment-PublicKey', publicSession.publicKey);
        }
    }

    buildErrorFromResponse(response) {
        const error = new Error(response.statusText);
        error.response = response;
        return error;
    }

    async __getPostResponse(url, data) {
        const token = localStorage.getItem('access_token');

        const headers = new Headers({
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
        });

        this.addStandardHeaderValues(headers);

        const response = await fetch(url, {
            method: 'POST',
            body: JSON.stringify(data),
            headers: headers
        });

        return response;
    }

    async post(url, data, withResponse) {
        const response = await this.__getPostResponse(url, data);

        if (response.ok) {
            if (withResponse) {
                return await response.json();
            }
            else {
                return { message: "Successfully Added" };
            }
        }
        else {
            throw this.buildErrorFromResponse(response);
        }
    }

    /** This function should be referenced for all NEW posts.
     * Instead of returning garbage JSON in the case that JSON can't be parsed, it returns void,
     * and does it without needing to explicitly pass a parameter.
     */
    async dynamicPost(url, data) {
        const response = await this.__getPostResponse(url, data);

        // Throw "standard" exception if response is not a 200.
        if (!response.ok) {
            throw this.buildErrorFromResponse(response);
        }

        // Attempt to parse the response as JSON, and return that JSON if it's there.
        try {
            const jsonResult = await response.json();
            return jsonResult;
        }
        catch (jsonParseErr) {
            // If parsing as JSON fails, return nothing (void).
            return;
        }
    }

    async put(url, data) {
        const token = localStorage.getItem('access_token');

        const headers = new Headers({
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
        });

        this.addStandardHeaderValues(headers);

        const response = await fetch(url, {
            method: 'PUT',
            body: JSON.stringify(data),
            headers: headers
        });

        if (response.ok) {
            return { message: "Successfully Added" };
        }
        else {
            throw this.buildErrorFromResponse(response);
        }
    }

    async delete(url) {
        const token = localStorage.getItem('access_token');

        const headers = new Headers({
            'Authorization': `Bearer ${token}`
        });

        this.addStandardHeaderValues(headers);

        const response = await fetch(url, {
            method: 'delete',
            headers: headers
        });

        if (response.ok) {
            return { message: "Successfully Deleted" };
        }
        else {
            const error = new Error(response.statusText);
            error.response = response;
            throw error;
        }
    }

    async deleteWithBody(url, data) {
        const token = localStorage.getItem('access_token');

        const headers = new Headers({
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
        });

        this.addStandardHeaderValues(headers);

        const response = await fetch(url, {
            method: 'delete',
            body: JSON.stringify(data),
            headers: headers
        });

        if (response.ok) {
            return { message: "Successfully Deleted" };
        }
        else {
            const error = new Error(response.statusText);
            error.response = response;
            throw error;
        }
    }
}

export const fetchBrary = new FetchBrary();